/**
 * The outreach (Phase 5, Task 18) slice of the wire — channel policy, messages,
 * consent and suppression, the compliance core of candidate outreach.
 *
 * Hand-written to mirror `openapi.yaml`'s `Outreach` tag, exactly as `recruiting.ts`'s
 * own header describes for its slice: transcribe what the screen reads, not the whole
 * contract, and this task does not touch `openapi.yaml`. A field the contract marks
 * `nullable` is `T | null` here, never omitted; a field a request marks optional is
 * optional here, because `exactOptionalPropertyTypes` is on and an absent value and an
 * explicit `null` are different facts (`PRODUCT.md` §4).
 *
 * Nothing here authorises anything. The channel-policy endpoints are
 * `workforce.manage_policies` at scope `company` (an admin surface — this decides which
 * channels the *company* may use at all); every message/consent/suppression endpoint is
 * `recruiting.manage_candidates` at scope `company`, the same code the candidates
 * surface uses. The server runs that chain on every call, whatever this screen renders.
 */

import { qs, type QueryValue } from './paths';

/* ──────────────────────────────────────────────────────────────── channels ── */

/**
 * `OutreachChannel` of `openapi.yaml`, verbatim. `email` is the only channel Task 18
 * can actually send through; the other five exist in the CHECK for a later sourcing
 * task's adapters and are default-disabled and always require approval.
 */
export const OUTREACH_CHANNELS = [
  'email',
  'linkedin_dm',
  'linkedin_comment',
  'facebook_dm',
  'facebook_comment',
  'web_scrape',
] as const;

export type OutreachChannel = (typeof OUTREACH_CHANNELS)[number];

/**
 * `ChannelPolicy` — one channel's outreach policy for this company. `updated_at` is
 * `null` for a channel the operator has never configured; the values shown are then
 * the closed default (`enabled: false`, `paused: false`, `daily_rate_limit: 3`,
 * `requires_approval: true`), never an inferred guess.
 */
export interface ChannelPolicy {
  readonly channel: OutreachChannel;
  readonly enabled: boolean;
  readonly paused: boolean;
  readonly daily_rate_limit: number;
  readonly requires_approval: boolean;
  readonly updated_at: string | null;
}

/** `GET /outreach/channel-policy` — always exactly six entries, one per channel. */
export interface ChannelPolicyListResponse {
  readonly data: readonly ChannelPolicy[];
}

/**
 * `ChannelPolicyUpsertRequest` — `channel` and `enabled` are required; the rest default
 * server-side when absent (`paused: false`, `daily_rate_limit: 3`,
 * `requires_approval: true`). `requires_approval` is forced back to `true` server-side
 * for every channel except `email`, regardless of what is sent here — this client
 * mirrors that in the UI (a locked control) but the server is the actual enforcement.
 */
export interface ChannelPolicyUpsertRequest {
  readonly channel: OutreachChannel;
  readonly enabled: boolean;
  readonly paused?: boolean;
  readonly daily_rate_limit?: number;
  readonly requires_approval?: boolean;
}

export const OUTREACH_CHANNEL_POLICY_PATH = '/api/v1/outreach/channel-policy';

/* ──────────────────────────────────────────────────────────────── messages ── */

/** `OutreachMessageStatus` of `openapi.yaml`, verbatim. */
export const OUTREACH_MESSAGE_STATUSES = [
  'draft',
  'pending_approval',
  'approved',
  'sent',
  'suppressed',
  'failed',
  'cancelled',
] as const;

export type OutreachMessageStatus = (typeof OUTREACH_MESSAGE_STATUSES)[number];

/**
 * `OutreachMessage` — a message to one candidate, addressed BY ID. The address used
 * at send time is read from the candidate row, never stored here. `consent_id` is the
 * live consent record the message will be gated on when the worker evaluates it, or
 * `null` when none exists yet.
 */
export interface OutreachMessage {
  readonly id: string;
  readonly candidate_id: string;
  readonly channel: OutreachChannel;
  readonly subject: string | null;
  readonly body: string;
  readonly status: OutreachMessageStatus;
  readonly consent_id: string | null;
  readonly approved_by: string | null;
  readonly approved_at: string | null;
  readonly sent_at: string | null;
  readonly error_message: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

/**
 * `OutreachMessageCreateRequest` — `candidate_id`, `channel` and `body` are required;
 * `subject` is present-or-absent. Opens in `pending_approval`.
 */
export interface OutreachMessageCreateRequest {
  readonly candidate_id: string;
  readonly channel: OutreachChannel;
  readonly subject?: string;
  readonly body: string;
}

export interface OutreachMessageListResponse {
  readonly data: readonly OutreachMessage[];
}

export const OUTREACH_MESSAGES_PATH = '/api/v1/outreach/messages';

/** `GET /outreach/messages?status=&limit=` — newest first, `limit` 1..500, default 100. */
export function outreachMessagesPath(query: {
  readonly status?: OutreachMessageStatus | undefined;
  readonly limit?: number | undefined;
}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    status: query.status,
    limit: query.limit,
  };
  return `${OUTREACH_MESSAGES_PATH}${qs(params)}`;
}

/** `POST /outreach/messages/{id}/approve` — `pending_approval` to `approved` only. */
export function outreachMessageApprovePath(id: string): string {
  return `${OUTREACH_MESSAGES_PATH}/${encodeURIComponent(id)}/approve`;
}

/* ───────────────────────────────────────────────────────────────── consent ── */

/** `OutreachConsentBasis` of `openapi.yaml`, verbatim. */
export const OUTREACH_CONSENT_BASES = [
  'candidate_initiated',
  'explicit_optin',
  'legitimate_interest',
] as const;

export type OutreachConsentBasis = (typeof OUTREACH_CONSENT_BASES)[number];

/** `OutreachConsent` — the stored consent record for one candidate + channel. */
export interface OutreachConsent {
  readonly id: string;
  readonly candidate_id: string;
  readonly channel: OutreachChannel;
  readonly basis: OutreachConsentBasis;
  readonly granted_at: string;
  readonly revoked_at: string | null;
  readonly note: string | null;
}

/**
 * `OutreachConsentCreateRequest` — `candidate_id`, `channel` and `basis` are required;
 * `note` is present-or-absent.
 */
export interface OutreachConsentCreateRequest {
  readonly candidate_id: string;
  readonly channel: OutreachChannel;
  readonly basis: OutreachConsentBasis;
  readonly note?: string;
}

export const OUTREACH_CONSENT_PATH = '/api/v1/outreach/consent';

/* ────────────────────────────────────────────────────────────── suppression ── */

/**
 * `OutreachSuppression` — never carries the raw address, only the channel, the reason
 * and when it was added. The address is hashed server-side from the candidate row.
 */
export interface OutreachSuppression {
  readonly id: string;
  readonly channel: OutreachChannel;
  readonly reason: string | null;
  readonly created_at: string;
}

/**
 * `OutreachSuppressionCreateRequest` — `candidate_id` and `channel` are required;
 * `reason` is present-or-absent. Idempotent — suppressing the same candidate+channel
 * twice returns the existing row rather than an error.
 */
export interface OutreachSuppressionCreateRequest {
  readonly candidate_id: string;
  readonly channel: OutreachChannel;
  readonly reason?: string;
}

export const OUTREACH_SUPPRESS_PATH = '/api/v1/outreach/suppress';
